import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, X, Loader2 } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { trackUpgradeModal } from "@/lib/analytics";
import { BETA_CONFIG } from "@/config/beta";
import { STRIPE_PRICE_IDS } from "@/config/stripe";
import { PRICE_LABELS, SAVINGS } from "@/config/pricing";

type UpgradeTrigger =
  | "goal_limit"
  | "habit_limit"
  | "coach_limit"
  | "plan_limit"
  | "mentor_message"
  | "integration"
  | "history"
  | "general";

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  trigger: UpgradeTrigger;
  integrationName?: string;
}

interface UpgradeContent {
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
}

const UPGRADE_CONTENT: Record<UpgradeTrigger, UpgradeContent> = {
  goal_limit: {
    icon: "🎯",
    title: "Unlock Unlimited Goals",
    subtitle: "You've reached your 3-goal limit",
    description:
      "Serious professionals need room to grow. Upgrade to Pro for unlimited goals and track every aspect of your career development.",
    features: [
      "Unlimited active goals",
      "Track Education, Exposure, and Experience goals",
      "Connect goals to your development plan",
    ],
  },
  habit_limit: {
    icon: "📋",
    title: "Unlock Unlimited Habits",
    subtitle: "You've reached your 5-habit limit",
    description:
      "Daily habits are the building blocks of career success. Upgrade to Pro for unlimited habits and build the momentum you need.",
    features: [
      "Unlimited daily habits",
      "90+ day habit history",
      "Advanced streak analytics",
    ],
  },
  coach_limit: {
    icon: "💡",
    title: "Unlock Weekly AI Insights",
    subtitle: "You've used your monthly AI analysis",
    description:
      "Pro members get fresh AI insights every week, plus daily Quick Wins to keep you on track.",
    features: [
      "Weekly AI Coach analysis",
      "Daily Quick Win suggestions",
      "Unlimited plan regeneration",
    ],
  },
  plan_limit: {
    icon: "📍",
    title: "Regenerate Your Development Plan",
    subtitle: "Free accounts get one plan generation",
    description:
      "Your goals and priorities change. Upgrade to Pro to regenerate your development plan whenever you need to pivot.",
    features: [
      "Unlimited plan regeneration",
      "AI-powered milestone adjustments",
      "Personalized action items",
    ],
  },
  mentor_message: {
    icon: "👥",
    title: "Connect with Mentors",
    subtitle: "Messaging requires Pro",
    description:
      "Unlock access to our community of mentors who've navigated the path you're on. Learn from their experience.",
    features: [
      "Message any mentor on the platform",
      "Offer your own mentorship",
      "Track mentorship conversations",
    ],
  },
  integration: {
    icon: "🔗",
    title: "Unlock Integration",
    subtitle: "Pro integration",
    description:
      "Bring MomentumEQ into your daily workflow with powerful integrations.",
    features: [
      "Slack bot for daily reminders",
      "Notion sync for seamless workflows",
      "Multi-calendar support",
    ],
  },
  history: {
    icon: "📊",
    title: "Unlock Full Habit History",
    subtitle: "See your complete journey",
    description:
      "Free accounts show 30 days of history. Upgrade to Pro to see 90+ days of data and understand your long-term patterns.",
    features: [
      "90+ day habit history",
      "Advanced analytics",
      "Trend analysis",
    ],
  },
  general: {
    icon: "⭐",
    title: "Upgrade to Pro",
    subtitle: "Accelerate your career",
    description:
      "Unlock the full power of MomentumEQ with unlimited goals, habits, AI insights, and integrations.",
    features: [
      "Unlimited goals and habits",
      "Weekly AI Coach insights",
      "Slack, Notion, and calendar integrations",
    ],
  },
};

// Price IDs from centralized config
const MONTHLY_PRICE_ID = STRIPE_PRICE_IDS.proMonthly;
const ANNUAL_PRICE_ID = STRIPE_PRICE_IDS.proAnnual;

export function UpgradeModal({
  isOpen,
  onClose,
  trigger,
  integrationName,
}: UpgradeModalProps) {
  const { createCheckout } = useSubscription();
  const [loading, setLoading] = useState<"monthly" | "annual" | null>(null);

  const content = UPGRADE_CONTENT[trigger];

  // Customize title for integration trigger
  const title = trigger === "integration" && integrationName
    ? `Unlock ${integrationName} Integration`
    : content.title;

  // Track when modal is shown
  useEffect(() => {
    if (isOpen) {
      trackUpgradeModal('shown', trigger);
    }
  }, [isOpen, trigger]);

  // Never show during beta
  if (BETA_CONFIG.isBetaMode) {
    return null;
  }

  const handleUpgrade = async (plan: "monthly" | "annual") => {
    trackUpgradeModal('cta_clicked', trigger, plan);
    setLoading(plan);
    try {
      const priceId = plan === "monthly" ? MONTHLY_PRICE_ID : ANNUAL_PRICE_ID;
      await createCheckout(priceId);
    } catch (error) {
      console.error("Checkout error:", error);
    } finally {
      setLoading(null);
    }
  };

  const handleClose = () => {
    trackUpgradeModal('dismissed', trigger);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[480px] border-primary/20 bg-gradient-to-b from-background to-background/95 p-0 overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none z-10"
        >
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </button>

        <div className="p-8 text-center">
          {/* Icon */}
          <div className="text-5xl mb-4">{content.icon}</div>

          {/* Header */}
          <DialogHeader className="space-y-2 mb-4">
            <DialogTitle className="text-2xl font-bold text-center">
              {title}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground italic text-center">
              {content.subtitle}
            </DialogDescription>
          </DialogHeader>

          {/* Description */}
          <p className="text-muted-foreground text-sm max-w-[400px] mx-auto mb-6">
            {content.description}
          </p>

          {/* Features list */}
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <ul className="space-y-3 text-left">
              {content.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
                    <Check className="h-3 w-3 text-accent" />
                  </div>
                  <span className="text-sm text-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Primary CTA - Monthly */}
          <Button
            onClick={() => handleUpgrade("monthly")}
            disabled={loading !== null}
            className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 transition-all duration-300"
          >
            {loading === "monthly" ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Redirecting...
              </>
            ) : (
              `Upgrade to Pro — ${PRICE_LABELS.pro.monthly}`
            )}
          </Button>

          {/* Annual option */}
          <div className="mt-4 space-y-2">
            <p className="text-sm text-muted-foreground">
              or save {SAVINGS.pro}% with annual ({PRICE_LABELS.pro.annual})
            </p>
            <Button
              variant="link"
              onClick={() => handleUpgrade("annual")}
              disabled={loading !== null}
              className="text-primary hover:text-primary/80"
            >
              {loading === "annual" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Redirecting...
                </>
              ) : (
                "Choose Annual"
              )}
            </Button>
          </div>

          {/* Maybe later */}
          <Button
            variant="ghost"
            onClick={handleClose}
            className="mt-2 text-muted-foreground hover:text-foreground"
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
